// src/pages/Resources.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { 
  ArrowLeft, 
  Search, 
  Download, 
  X, 
  FileText, 
  BookOpen,
  Folder,
  Crown,
  Sparkles,
  Loader2
} from 'lucide-react';
import { 
  filterResourcesBySearch, 
  buildDisplayCategories, 
  formatFileSize,
  getCategoryConfig,
  getCategoryColorClasses 
} from '../utils/resourcesHelper';

function Resources() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Resources data
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
  
  // Display categories (built dynamically from resources)
  const [displayCategories, setDisplayCategories] = useState([]);

  // Load resources from Firestore (real-time listener)
  useEffect(() => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    // Query active resources, ordered by order field
    const q = query(
      collection(db, 'resourceFiles'),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const resourcesData = [];
        snapshot.forEach((doc) => {
          resourcesData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setResources(resourcesData);
        setDisplayCategories(buildDisplayCategories(resourcesData));
        setIsLoading(false);
      },
      (err) => {
        console.error('Error loading resources:', err);
        setError('Failed to load resources. Please try again.');
        setIsLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [user]);

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter resources whenever search term or category changes
  useEffect(() => {
    let filtered = resources;

    // Filter by category first
    if (activeCategory !== 'All') {
      filtered = filtered.filter(r => 
        r.categories && r.categories.includes(activeCategory)
      );
    }

    // Then filter by search term
    if (debouncedSearch) {
      filtered = filterResourcesBySearch(filtered, debouncedSearch);
    }

    setFilteredResources(filtered);
  }, [resources, activeCategory, debouncedSearch]);

  // Handle file open — opens in a new tab reliably without being blocked
  const handleDownload = async (resource) => {
    setDownloadingId(resource.id);

    // Open the new tab SYNCHRONOUSLY here during the click event.
    // Browsers only allow window.open() without blocking when it's called
    // directly from a user interaction — not after an async/await delay.
    const newWindow = window.open('', '_blank');

    if (newWindow) {
      newWindow.document.title = 'Loading...';
    }

    try {
      const storageRef = ref(storage, resource.storagePath);
      const url = await getDownloadURL(storageRef);

      if (newWindow) {
        // Point the already-open tab to the real URL
        newWindow.location.href = url;
      } else {
        // Last-resort fallback: if the window was somehow still blocked,
        // navigate the current tab (very unlikely to reach here)
        window.location.href = url;
      }
    } catch (err) {
      console.error('Download error:', err);
      if (newWindow) {
        newWindow.close(); // Clean up the blank tab on failure
      }
      setError(`Failed to open ${resource.fileName}. Please try again.`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloadingId(null);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearch('');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="flex items-center justify-center mb-4">
            <Crown className="text-purple-500 animate-bounce" size={32} />
            <Sparkles className="text-yellow-400 animate-pulse" size={24} />
          </div>
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold mb-2">Loading Resources</div>
          <div className="text-gray-600">Preparing your educational materials...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      <div className="max-w-7xl mx-auto">
        
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
            aria-label="Go back home"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <BookOpen size={32} />
            Resources
          </h1>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
            <div className="text-red-500 text-2xl">⚠️</div>
            <div className="flex-1">
              <p className="text-red-800 font-semibold">Error</p>
              <p className="text-red-600">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, description, or filename..."
              className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-white/20 bg-white/95 backdrop-blur-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all duration-200"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Clear search"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {displayCategories.map((category) => {
            const isActive = activeCategory === category.label;
            const CategoryIcon = category.icon;
            
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.label)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm
                  transition-all duration-200 transform hover:scale-105
                  ${getCategoryColorClasses(category.color, isActive)}
                  ${isActive ? 'shadow-lg' : 'shadow-md'}
                `}
              >
                <CategoryIcon size={16} />
                <span>{category.label}</span>
                <span className="ml-1 opacity-75">({category.count})</span>
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        <p className="text-white text-lg mb-4 font-semibold">
          Showing {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'}
        </p>

        {/* Resources Grid or Empty State */}
        {filteredResources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onDownload={handleDownload}
                isDownloading={downloadingId === resource.id}
              />
            ))}
          </div>
        ) : (
          <EmptyState 
            hasResources={resources.length > 0}
            hasSearch={debouncedSearch.length > 0}
            activeCategory={activeCategory}
          />
        )}
      </div>
    </div>
  );
}

// Resource Card Component
function ResourceCard({ resource, onDownload, isDownloading }) {
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex flex-col">
      {/* Icon and Title */}
      <div className="flex items-start gap-3 mb-3">
        <FileText className="text-purple-600 flex-shrink-0" size={24} />
        <h3 className="text-lg font-bold text-gray-800 leading-tight flex-1">
          {resource.title}
        </h3>
      </div>

      {/* Description */}
      {resource.description && (
        <p className="text-gray-600 text-sm mb-4 flex-1">
          {resource.description}
        </p>
      )}

      {/* Category Badges */}
      {resource.categories && resource.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {resource.categories.map((category, index) => {
            const config = getCategoryConfig(category);
            const colorClasses = getCategoryColorClasses(config.color, false);
            
            return (
              <span
                key={index}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${colorClasses}`}
              >
                {category}
              </span>
            );
          })}
        </div>
      )}

      {/* File Size and Download Button */}
      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200">
        <span className="text-sm text-gray-500 font-medium">
          {formatFileSize(resource.fileSize)}
        </span>
        
        <button
          onClick={() => onDownload(resource)}
          disabled={isDownloading}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl font-semibold text-sm transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
        >
          {isDownloading ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <Download size={16} />
              <span>Download</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ hasResources, hasSearch, activeCategory }) {
  // No resources exist at all
  if (!hasResources) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-12 shadow-xl text-center">
        <BookOpen className="mx-auto mb-4 text-purple-500" size={64} />
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Resources Coming Soon!</h3>
        <p className="text-gray-600">
          Check back soon for downloadable educational materials to support your Arabic learning journey.
        </p>
      </div>
    );
  }

  // Search returned no results
  if (hasSearch) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-12 shadow-xl text-center">
        <Search className="mx-auto mb-4 text-gray-400" size={64} />
        <h3 className="text-2xl font-bold text-gray-800 mb-2">No Resources Match Your Search</h3>
        <p className="text-gray-600">
          Try different keywords or browse all resources by clearing your search.
        </p>
      </div>
    );
  }

  // Category filter shows no results
  if (activeCategory !== 'All') {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-12 shadow-xl text-center">
        <Folder className="mx-auto mb-4 text-gray-400" size={64} />
        <h3 className="text-2xl font-bold text-gray-800 mb-2">No Resources in This Category Yet</h3>
        <p className="text-gray-600">
          Browse other categories or check back soon for new materials.
        </p>
      </div>
    );
  }

  // Fallback (shouldn't reach here)
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-12 shadow-xl text-center">
      <FileText className="mx-auto mb-4 text-gray-400" size={64} />
      <h3 className="text-2xl font-bold text-gray-800 mb-2">No Resources Available</h3>
      <p className="text-gray-600">
        Please check back later.
      </p>
    </div>
  );
}

export default Resources;